'use client';

import { FileText } from 'lucide-react';
import { useAppRouter } from '~/hooks';

export default function TermsOfServicePage() {
  const router = useAppRouter();

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-foreground">Terms of Service</h1>
          </div>
          <p className="text-foreground-secondary">
            Last updated: December 5, 2025
          </p>
        </div>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-foreground mb-4 font-bold">
              1. Agreement to Terms
            </h2>
            <p className="text-foreground-secondary mb-4">
              By accessing and using Leadbunker (&quot;Service&quot;), you
              accept and agree to be bound by the terms and provision of this
              agreement. If you do not agree to abide by the above, please do
              not use this service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-foreground mb-4 font-bold">2. Use License</h2>
            <p className="text-foreground-secondary mb-4">
              Permission is granted to temporarily use Leadbunker for personal
              or commercial purposes. This is the grant of a license, not a
              transfer of title, and under this license you may not:
            </p>
            <ul className="list-disc pl-6 text-foreground-secondary space-y-2 mb-4">
              <li>Modify or copy the materials</li>
              <li>
                Use the materials for any commercial purpose without proper
                licensing
              </li>
              <li>
                Attempt to decompile or reverse engineer any software contained
                in Leadbunker
              </li>
              <li>
                Remove any copyright or other proprietary notations from the
                materials
              </li>
              <li>
                Transfer the materials to another person or &quot;mirror&quot;
                the materials on any other server
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-foreground mb-4 font-bold">
              3. Acceptable Use
            </h2>
            <p className="text-foreground-secondary mb-4">
              You agree to use Leadbunker only for lawful purposes. You are
              prohibited from:
            </p>
            <ul className="list-disc pl-6 text-foreground-secondary space-y-2 mb-4">
              <li>
                Scraping websites that explicitly prohibit scraping in their
                terms of service or robots.txt
              </li>
              <li>
                Using the service to collect personal information in violation
                of privacy laws
              </li>
              <li>
                Overloading or attempting to gain unauthorized access to any
                systems
              </li>
              <li>
                Using the scraped data for spam, harassment, or illegal
                activities
              </li>
              <li>
                Violating any applicable local, state, national, or
                international law
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-foreground mb-4 font-bold">
              4. User Responsibilities
            </h2>
            <p className="text-foreground-secondary mb-4">
              You are solely responsible for ensuring that your use of
              Leadbunker complies with all applicable laws and regulations,
              including but not limited to data protection laws, copyright laws,
              and website terms of service.
            </p>
            <p className="text-foreground-secondary mb-4">
              You acknowledge that scraping certain websites may violate their
              terms of service or applicable laws. It is your responsibility to
              obtain necessary permissions before scraping any website.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-foreground mb-4 font-bold">5. Disclaimer</h2>
            <p className="text-foreground-secondary mb-4">
              The materials on Leadbunker are provided on an &apos;as is&apos;
              basis. Leadbunker makes no warranties, expressed or implied, and
              hereby disclaims and negates all other warranties including,
              without limitation, implied warranties or conditions of
              merchantability, fitness for a particular purpose, or
              non-infringement of intellectual property or other violation of
              rights.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-foreground mb-4 font-bold">6. Limitations</h2>
            <p className="text-foreground-secondary mb-4">
              In no event shall Leadbunker or its suppliers be liable for any
              damages (including, without limitation, damages for loss of data
              or profit, or due to business interruption) arising out of the use
              or inability to use Leadbunker, even if Leadbunker or a Leadbunker
              authorized representative has been notified orally or in writing
              of the possibility of such damage.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-foreground mb-4 font-bold">7. Account Terms</h2>
            <p className="text-foreground-secondary mb-4">
              You are responsible for maintaining the security of your account
              and password. Leadbunker cannot and will not be liable for any
              loss or damage from your failure to comply with this security
              obligation.
            </p>
            <ul className="list-disc pl-6 text-foreground-secondary space-y-2 mb-4">
              <li>
                You must provide accurate and complete information when creating
                an account
              </li>
              <li>
                You are responsible for all activities that occur under your
                account
              </li>
              <li>You must not share your account credentials with others</li>
              <li>
                You must notify us immediately of any unauthorized use of your
                account
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-foreground mb-4 font-bold">
              8. Service Modifications
            </h2>
            <p className="text-foreground-secondary mb-4">
              Leadbunker reserves the right to modify or discontinue,
              temporarily or permanently, the Service (or any part thereof) with
              or without notice. Leadbunker shall not be liable to you or to any
              third party for any modification, price change, suspension, or
              discontinuance of the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-foreground mb-4 font-bold">9. Termination</h2>
            <p className="text-foreground-secondary mb-4">
              We may terminate or suspend your account and bar access to the
              Service immediately, without prior notice or liability, under our
              sole discretion, for any reason whatsoever, including without
              limitation if you breach the Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-foreground mb-4 font-bold">
              10. Governing Law
            </h2>
            <p className="text-foreground-secondary mb-4">
              These Terms shall be governed and construed in accordance with the
              laws of the jurisdiction in which Leadbunker operates, without
              regard to its conflict of law provisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-foreground mb-4 font-bold">
              11. Changes to Terms
            </h2>
            <p className="text-foreground-secondary mb-4">
              We reserve the right, at our sole discretion, to modify or replace
              these Terms at any time. If a revision is material, we will
              provide at least 30 days&apos; notice prior to any new terms
              taking effect.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-foreground mb-4 font-bold">
              12. Contact Information
            </h2>
            <p className="text-foreground-secondary mb-4">
              If you have any questions about these Terms, please contact us:
            </p>
            <ul className="list-disc pl-6 text-foreground-secondary space-y-2 mb-4">
              <li>By email: legal@leadbunker.com</li>
              <li>By visiting our contact page</li>
            </ul>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-card-border">
          <button
            onClick={() => router.push('/')}
            className="text-primary hover:opacity-80 transition-opacity"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </>
  );
}
